// P:\0PJ\synforge\frontend\lib\api\misc.ts v2 2026.10.30 21:50
//   v2: authAPI.register 에 약관 동의값(terms_version, consent_items) 추가.
// P:\0PJ\synforge\frontend\lib\api\misc.ts v1 2026.08.26
import axios from "axios"
import { api } from "./core"

export const authAPI = {
  login:               (email: string, password: string) =>
    api.post("/auth/login/", { email, password }),
  register:            (
    name: string, email: string, phone: string, password: string,
    formFillSeconds?: number, screenResolution?: string, browserTimezone?: string,
    termsVersion?: string, consentItems?: Record<string, boolean>,
  ) =>
    api.post("/auth/register/", {
      name, email, phone, password, password2: password,
      form_fill_seconds: formFillSeconds,
      screen_resolution: screenResolution,
      browser_timezone: browserTimezone,
      terms_version: termsVersion,
      consent_items: consentItems,
    }),
  sendPhoneCode:       (phone: string) => api.post("/auth/send-phone-code/", { phone }),
  verifyPhoneCode:     (phone: string, code: string) => api.post("/auth/verify-phone-code/", { phone, code }),
  logout:              () => {
    localStorage.removeItem("access_token")
    localStorage.removeItem("refresh_token")
    return api.post("/auth/logout/")
  },
  me:                  () => api.get("/auth/me/"),
  updateMe:            (name: string) => api.patch("/auth/me/", { name }),
  updateAddress: (data: {
    country?: string; state_province?: string; city?: string;
    postal_code?: string; address_line?: string;
  }) => api.patch("/auth/me/", data),
  verifyLoginCode:     (email: string, code: string) => api.post("/auth/login/verify/", { email, code }),
  resendLoginCode:     (email: string) => api.post("/auth/login/resend/", { email }),
  uploadAvatar:        (file: File) => {
    const formData = new FormData()
    formData.append("file", file)
    return api.post("/auth/me/avatar/", formData, { headers: { "Content-Type": undefined } })
  },
  removeAvatar:        () => api.delete("/auth/me/avatar/remove/"),
  resendVerification:  (email: string) => api.post("/auth/resend-verification/", { email }),
  exportMyData:        () => api.post("/auth/me/export/"),
  deleteMyAccount:     () => api.post("/auth/me/delete/"),
}

export const aiAPI = {
  getModels:           () => api.get("/ai/models/"),
  getRoles:            () => api.get("/ai/prompt-rules/"),
  getStatus:           () => api.get("/ai/status/"),
  getUsage:            () => api.get("/ai/usage/"),
  getAdminUsage:       () => api.get("/admin/usage/"),
  getRoleTranslations: (locale: string) => api.get(`/ai/role-translations/?locale=${locale}`),
  getOptionalModels:   () => api.get("/ai/optional-models/"),
  saveOptionalModels:  (enabledIds: string[]) =>
    api.post("/ai/optional-models/", { enabled_ids: enabledIds }),
}

export const dashboardAPI = {
  get:   () => api.get("/dashboard/"),
  stats: () => api.get("/stats/"),
}

export const classifierAPI = {
  classify:      (agenda: string, models?: string[], files?: { base64: string; mime_type: string; name: string }[]) =>
    api.post("/classify/", { agenda, ...(models ? { models } : {}), ...(files && files.length > 0 ? { files } : {}) }),
  extractPDF:    async (file: File) => {
    const formData = new FormData()
    formData.append("file", file)
    const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : ""
    return axios.post(
      `${process.env.NEXT_PUBLIC_API_URL || "http://127.0.0.1:8000/api"}/extract-pdf/`,
      formData,
      { headers: { "Authorization": `Bearer ${token}` } }
    )
  },
  uploadFiles:   (files: { base64: string; mime_type: string; name: string }[]) =>
    api.post("/upload-files/", { files }),
  extractStatus: (taskId: string) =>
    api.get(`/extract-status/${taskId}/`),
}

export const analysisAPI = {
  create: (agenda: string, sourceConversationId?: number) =>
    api.post("/analysis/", {
      agenda,
      source_conversation_id: sourceConversationId ?? null,
    }),
  status: (id: string) => api.get(`/analysis/${id}/status/`),
  confirm: (id: string, revisedAgenda?: string) =>
    api.post(`/analysis/${id}/confirm/`, { revised_agenda: revisedAgenda ?? "" }),
}

export const instantAPI = {
  ask: (question: string, conversationId?: number) =>
    api.post("/instant/ask/", {
      question,
      conversation_id: conversationId ?? null,
    }),
  escalate: (conversationId: number, route: string) =>
    api.post("/instant/escalate/", {
      conversation_id: conversationId,
      route,
    }),
  conversations: (trash?: boolean) =>
    api.get(`/instant/conversations/${trash ? "?view=trash" : ""}`),
  restoreConversation: (conversationId: number) =>
    api.post(`/instant/conversations/${conversationId}/restore/`),
  conversationDetail: (conversationId: number) =>
    api.get(`/instant/conversations/${conversationId}/`),
  renameConversation: (conversationId: number, title: string) =>
    api.patch(`/instant/conversations/${conversationId}/`, { title }),
  deleteConversation: (conversationId: number) =>
    api.delete(`/instant/conversations/${conversationId}/`),
}

export const resultsAPI = {
  list:            () => api.get("/results/list/"),
  trash:           () => api.get("/results/trash/"),
  get:             (resultId: string) => api.get(`/results/${resultId}/`),
  delete:          (resultId: string) => api.post(`/results/${resultId}/delete/`),
  restore:         (resultId: string) => api.post(`/results/${resultId}/restore/`),
  permanentDelete: (resultId: string) => api.post(`/results/${resultId}/permanent/`),
  sessionResults:  (sessionId: string) => api.get(`/results/session/${sessionId}/`),
}

export const translationAPI = {
  translate: (turnId: string, targetLanguage: string) =>
    api.post("/translation/translate/", {
      turn_id: turnId,
      target_language: targetLanguage,
    }),
  // 회의ID(=세션 폴더명) + 파일 URL 로 번역 요청 — 백엔드가 세션 폴더 안
  //   실제 파일에서 원문을 읽는다. 기존 translate(turn_id) 는 그대로 둔다(2026.10.03).
  translateFile: (sessionId: string, fileUrl: string, targetLanguage: string) =>
    api.post("/translation/translate/", {
      session_id: sessionId,
      file_url: fileUrl,
      target_language: targetLanguage,
    }),
  // 이미 번역된 언어 목록 — { done: { ja: { file_url, file_name }, ... } } (2026.10.03 14:11)
  status: (sessionId: string, fileUrl: string) =>
    api.get("/translation/status/", {
      params: { session_id: sessionId, file_url: fileUrl },
    }),
}

export const projectAPI = {
  list:   () => api.get("/projects/"),
  create: (name: string, goal?: string) =>
    api.post("/projects/", { name, goal: goal || "" }),
  detail: (projectId: string) => api.get(`/projects/${projectId}/`),
  update: (projectId: string, data: Partial<{ name: string; goal: string; instructions: string; memory: string; status: string }>) =>
    api.patch(`/projects/${projectId}/`, data),
  remove: (projectId: string) => api.delete(`/projects/${projectId}/`),
  uploadFiles: (projectId: string, files: { name: string; base64: string }[]) =>
    api.post(`/projects/${projectId}/files/`, { files }),
  deleteFile: (projectId: string, fileId: string) =>
    api.delete(`/projects/${projectId}/files/${fileId}/`),
  attach: (projectId: string, handoffId: string) =>
    api.post(`/projects/${projectId}/attach/`, { handoff_id: handoffId }),
}